import db from '../utils/Database';

/**
 * Service de gestion des données pour l'application
 * Fournit des méthodes CRUD et des fonctionnalités avancées pour manipuler les données de l'application
 */
class DataService {
  /**
   * Initialise le service de données avec la base de données et configure la pagination
   */
  constructor() {
    this.db = db;
    this.pageSize = 10; // Taille par défaut pour la pagination
  }

  // Méthodes génériques CRUD
  async getById(table, id) {
    return await this.db[table].get(id);
  }

  async getAll(table, page = 0, pageSize = this.pageSize) {
    if (pageSize === null || pageSize <= 0) {
        return await this.db[table].toArray();
    }
    const offset = page * pageSize;
    return await this.db[table]
      .offset(offset)
      .limit(pageSize)
      .toArray();
  }

  async add(table, data) {
    const id = await this.db[table].add(data);
    return await this.getById(table, id);
  }

  async update(table, id, data) {
    await this.db[table].update(id, data);
    return await this.getById(table, id);
  }

  async delete(table, id) {
    return await this.db[table].delete(id);
  }

  //Métodes getByField (store, field, value)
  async getByField(table, field, value) {
    return await this.db[table]
      .where(field)
      .equals(value)
      .first();
  }

  // Méthodes spécifiques aux relations
  async getAccountWithDetails(accountId) {
    const account = await this.getById('accounts', accountId);
    if (!account) return null;

    // Charger les adresses avec toutes les propriétés
    const locations = await this.db.locations
      .where('accountId')
      .equals(accountId)
      .toArray();

    const addresses = locations.map(loc => ({
      id: loc.id,
      address: loc.address,
      postalCode: loc.postalCode,
      city: loc.city,
      isPrimary: loc.isPrimary,
      isBillingLocation: loc.isBillingLocation,
      accountId: loc.accountId,
      distanceKm: loc.distanceKm
    }));

    // Charger les contacts avec toutes les propriétés
    const contacts = await this.db.contacts
      .where('accountId')
      .equals(accountId)
      .toArray();

    // Retourner l'objet complet avec les tableaux addresses et contacts
    const result = {
      ...account,
      addresses: addresses.length > 0 ? addresses : [{ address: '', postalCode: '', city: '', isPrimary: true, isBillingLocation: true, distanceKm: 0 }],
      contacts: contacts.length > 0 ? contacts : [{ firstName: '', lastName: '', email: '', phone: '', isPrimary: true, isBillingContact: true }]
    };
    return result;
  }

  async getOpportunityWithQuotes(opportunityId) {
    const opportunity = await this.getById('opportunities', opportunityId);
    if (!opportunity) return null;

    const quotes = await this.db.quotes
      .where('opportunityId')
      .equals(opportunityId)
      .toArray();

    return {
      ...opportunity,
      quotes
    };
  }

  async getQuoteWithLines(quoteId) {
    try {
        const quote = await this.getById('quotes', quoteId);

        if (!quote) return { lines: [] };

        const lines = await this.getQuoteLineHierarchy(quoteId);

        const bill = await this.db.bills
            .where('quoteId')
            .equals(quoteId)
            .first();

        return {
            ...quote,
            lines,
            bill
        };
    } catch (error) {
        console.error('Error in getQuoteWithLines:', error);
        return { lines: [] };
    }
  }

  async getQuoteByCustomId(quoteCustomId) {
    const quote = await this.db.quotes
      .where('quoteCustomId')
      .equals(quoteCustomId)
      .first();
    return quote;
  }

  async getBillWithLines(billId) {
    const bill = await this.getById('bills', billId);
    if (!bill) return null;

    const lines = await this.getBillLineHierarchy(billId);

    return {
      ...bill,
      lines
    };
  }

  async getBillByCustomId(billCustomId) {
    return await this.db.bills
      .where('billCustomId')
      .equals(billCustomId)
      .first();
  }

  // Méthodes pour gérer les hiérarchies de lignes
  async getQuoteLineHierarchy(quoteId, parentLineId = null) {
    const lines = await this.db.quoteLines
      .where('quoteId')
      .equals(quoteId)
      .and(line => line.parentLineId === parentLineId)
      .sortBy('position');

    for (let line of lines) {
      line.subLines = await this.getQuoteLineHierarchy(quoteId, line.id);
    }

    return lines;
  }

  async updateQuoteLinePosition(lineId, newPosition, quoteId, parentLineId = null) {
    try {
      const lines = await this.db.quoteLines
        .where('quoteId')
        .equals(quoteId)
        .and(line => line.parentLineId === parentLineId)
        .toArray();

      const currentLine = lines.find(line => line.id === lineId);
      if (!currentLine) return;

      const oldPosition = currentLine.position;
      
      // Mettre à jour les positions des autres lignes
      for (const line of lines) {
        if (line.id === lineId) {
          await this.db.quoteLines.update(line.id, { position: newPosition });
        } else if (oldPosition < newPosition && line.position > oldPosition && line.position <= newPosition) {
          await this.db.quoteLines.update(line.id, { position: line.position - 1 });
        } else if (oldPosition > newPosition && line.position >= newPosition && line.position < oldPosition) {
          await this.db.quoteLines.update(line.id, { position: line.position + 1 });
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating quote line position:', error);
      return false;
    }
  }

  async getOptionProductId() {
    const optionProduct = await db.products.where('name').equals('OPTIONLINE').first();
    return optionProduct ? parseInt(optionProduct.id) : null;
  }

  async getBillLineHierarchy(billId, parentLineId = null) {
    const lines = await this.db.billLines
      .where('billId')
      .equals(billId)
      .and(line => line.parentLineId === parentLineId)
      .toArray();

    for (let line of lines) {
      line.subLines = await this.getBillLineHierarchy(billId, line.id);
    }

    return lines;
  }

  // Méthodes de recherche avancée
  async search(table, query, fields, page = 0, pageSize = this.pageSize) {
    const offset = page * pageSize;
    const lowerQuery = query.toLowerCase();

    return await this.db[table]
      .filter(item =>
        fields.some(field =>
          item[field] && item[field].toString().toLowerCase().includes(lowerQuery)
        )
      )
      .offset(offset)
      .limit(pageSize)
      .toArray();
  }

  // Méthodes de comptage pour la pagination
  async count(table) {
    return await this.db[table].count();
  }

  async getTotalPages(table, pageSize = this.pageSize) {
    const total = await this.count(table);
    return Math.ceil(total / pageSize);
  }

  // Méthodes de filtrage
  async filter(table, conditions, page = 0, pageSize = this.pageSize) {
    const offset = page * pageSize;

    return await this.db[table]
      .filter(item =>
        Object.entries(conditions).every(([key, value]) => item[key] === value)
      )
      .offset(offset)
      .limit(pageSize)
      .toArray();
  }

  // Méthodes de tri
  async getSorted(table, field, ascending = true, page = 0, pageSize = this.pageSize) {
    const offset = page * pageSize;

    return await this.db[table]
      .orderBy(field)
      .reverse(!ascending)
      .offset(offset)
      .limit(pageSize)
      .toArray();
  }

  async getTypes(typeName) {
    const typeSettings = await this.db.settingTypes
      .where('name')
      .equals(typeName)
      .first();

    if (!typeSettings) return [];

    const types = await this.db.settings
      .where('typeId')
      .equals(typeSettings.id)
      .sortBy('order');

    // On retourne un tableau d'objets avec id et name
    return types.map(type => ({
      id: type.id,
      name: type.name,
      data: type.data
    }));
  }

  async getBillingContact(accountId) {
    return await this.db.contacts
      .where('accountId')
      .equals(accountId)
      .and(contact => contact.isBillingContact)
      .first();
  }

  async getBillingLocation(accountId) {
    return await this.db.locations
      .where('accountId')
      .equals(accountId)
      .and(location => location.isBillingLocation)
      .first();
  }

  async setBillingContact(accountId, contactId) {
    // Réinitialiser tous les contacts de facturation pour ce compte
    const contacts = await this.db.contacts
      .where('accountId')
      .equals(accountId)
      .toArray();
    
    for (const contact of contacts) {
      await this.db.contacts.update(contact.id, { isBillingContact: contact.id === contactId });
    }
  }

  async setBillingLocation(accountId, locationId) {
    // Réinitialiser toutes les adresses de facturation pour ce compte
    const locations = await this.db.locations
      .where('accountId')
      .equals(accountId)
      .toArray();
    
    for (const location of locations) {
      await this.db.locations.update(location.id, { isBillingLocation: location.id === locationId });
    }
  }

  /**
   * Sauvegarde un compte avec ses adresses et contacts
   * @param {Object} accountData - Les données du compte
   * @returns {Promise<Object>} - Le compte sauvegardé
   */
  async saveAccount(accountData) {
    try {
      // Version simplifiée pour mettre en évidence le traitement du taxId
      let account;
      
      // Utilisation directe de db au lieu de this.db pour éviter toute confusion
      if (accountData.id) {
        // Pour une mise à jour, récupérer le compte existant
        const existingAccount = await db.accounts.get(accountData.id);
        
        // Mettre à jour directement les propriétés
        existingAccount.name = accountData.name;
        existingAccount.typeId = accountData.typeId;
        existingAccount.taxId = accountData.taxId || ''; // Assurer que taxId est défini
        
        // Sauvegarder
        await db.accounts.update(accountData.id, existingAccount);
        account = existingAccount;
      } else {
        // Pour une création, créer l'objet de base
        const newAccount = {
          name: accountData.name,
          typeId: accountData.typeId,
          taxId: accountData.taxId || '' // Assurer que taxId est défini
        };
        
        // Sauvegarder et récupérer l'ID
        const id = await db.accounts.add(newAccount);
        account = { ...newAccount, id };
      }
      
      // Traiter les adresses et contacts comme avant
      // ...existing code...
      
      // Retourner le compte mis à jour avec ses relations
      return await this.getAccountWithDetails(account.id);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du compte:', error);
      throw error;
    }
  }

  /**
   * Récupère le compte principal (entreprise) avec ses contacts et adresses
   * @returns {Promise<Object>} Compte principal avec contacts et adresses
   */
  async getCompanyAccount() {
    try {
      // Récupérer d'abord le type "AccountType"
      const accountType = await this.db.settingTypes.where('name').equals('AccountType').first();
      
      if (!accountType) {
        console.error("Type 'AccountType' non trouvé");
        return null;
      }
      
      // Récupérer ensuite le setting "Principal" associé à ce type
      const principalSetting = await this.db.settings
        .where('typeId').equals(accountType.id)
        .and(setting => setting.name === 'Principal')
        .first();
      
      if (!principalSetting) {
        console.error("Paramètre 'Principal' non trouvé");
        return null;
      }
      
      // Récupérer le compte avec ce typeId (qui est stocké dans principalSetting.id)
      const companyAccount = await this.db.accounts.where('typeId').equals(principalSetting.id).first();
      
      if (!companyAccount) {
        console.error("Aucun compte avec typeId 'Principal' trouvé");
        return null;
      }
      
      // Récupérer les contacts et adresses associés
      const contacts = await this.db.contacts.where('accountId').equals(companyAccount.id).toArray();
      const locations = await this.db.locations.where('accountId').equals(companyAccount.id).toArray();
      
      // Enrichir l'objet compte
      companyAccount.contacts = contacts;
      companyAccount.addresses = locations;
      
      return companyAccount;
    } catch (error) {
      console.error("Erreur lors de la récupération du compte d'entreprise:", error);
      return null;
    }
  }
}

// Créer une instance nommée avant l'export
const dataService = new DataService();

export default dataService;
